import { createCustomMarkerIcon, MARKER_TYPES } from './markerIcons';
import mapboxgl from 'mapbox-gl';

/**
 * Configura los event handlers del mapa (clicks, hover, feature state)
 * Las capas y sources ya vienen del estilo JSON centralizado
 */
export const setupMapEventHandlers = (map, onParcelClickRef, onLotClickRef, projectId = null) => {
  // Verificar que el mapa y el estilo estén completamente cargados
  if (!map.isStyleLoaded()) {
    map.once('styledata', () => {
      if (map.isStyleLoaded()) {
        setTimeout(() => {
          setupMapEventHandlers(map, onParcelClickRef, onLotClickRef, projectId);
        }, 100);
      }
    });
    return;
  }

  // Note: click handler cleanup is handled inside setupLotesClickHandler
  // using stored function references for proper removal

  // If a specific project is selected, try to zoom to its extent
  if (projectId) {
    try {
      map.once('idle', () => {
        try {
          const features = map.querySourceFeatures('cloud4geo', {
            sourceLayer: 'lotes_areas',
            filter: ['==', ['get', 'project_id'], projectId]
          });

          if (features && features.length > 0) {
            let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
            for (const f of features) {
              const geom = f.geometry;
              if (!geom || !geom.coordinates) continue;
              const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.type === 'MultiPolygon' ? geom.coordinates : [];
              for (const poly of polys) {
                const ring = poly[0] || [];
                for (const coord of ring) {
                  const [lng, lat] = coord;
                  minLng = Math.min(minLng, lng);
                  minLat = Math.min(minLat, lat);
                  maxLng = Math.max(maxLng, lng);
                  maxLat = Math.max(maxLat, lat);
                }
              }
            }

            if (minLng !== Infinity) {
              const bounds = [[minLng, minLat], [maxLng, maxLat]];
              map.fitBounds(bounds, { padding: 50, maxZoom: 20 });
            }
          }
        } catch (err) {
          console.warn("Error fitting to project bounds:", err);
        }
      });
    } catch (err) {
      console.warn("Error setting up project bounds fitting:", err);
    }
  }

  // Add click handler for lotes status layer
  // Store handler reference so we can properly remove it
  let lotesClickHandler = null;

  const setupLotesClickHandler = () => {
    if (!map.getLayer('lotes-layer-status')) {
      console.warn('%cMap/util.js: lotes-layer-status not found, waiting...', 'color: #FF9800;');
      map.once('idle', () => {
        if (map.getLayer('lotes-layer-status')) {
          setupLotesClickHandler();
        }
      });
      return;
    }

    // Remove previous handler using the stored reference
    if (lotesClickHandler) {
      map.off('click', 'lotes-layer-status', lotesClickHandler);
    }

    lotesClickHandler = (e) => {
      try {
        if (!e.features || e.features.length === 0) return;

        const feature = e.features[0];
        const featureId = feature.id;

        if (featureId !== undefined && featureId !== null && map) {
          try {
            clearAllHighlights(map);
            map.setFeatureState({
              source: 'cloud4geo',
              sourceLayer: 'lotes_areas',
              id: featureId
            }, {
              selected: true
            });
          } catch (highlightError) {
            console.error('%cMap/util.js: Error setting feature state:', 'color: #FF6B6B;', highlightError);
          }
        }

        const convertedFeature = {
          type: 'Feature',
          id: feature.id,
          properties: feature.properties,
          geometry: feature.geometry
        };

        const callback = onLotClickRef?.current || onLotClickRef;

        if (callback && typeof callback === 'function') {
          try {
            callback(convertedFeature);
          } catch (callbackError) {
            console.error('%cMap/util.js: Error executing onLotClickRef:', 'color: #FF6B6B;', callbackError);
          }
        }
      } catch (err) {
        console.warn("Lotes layer click handler error:", err);
      }
    };

    map.on('click', 'lotes-layer-status', lotesClickHandler);
  };

  // Setup click handler - only once, preferring immediate if layer exists
  if (map.getLayer('lotes-layer-status')) {
    setupLotesClickHandler();
  } else {
    map.once('idle', () => {
      setupLotesClickHandler();
    });
  }

  // Track hovered feature ID
  let hoveredFeatureId = null;

  // Hover effects for lotes
  map.on('mouseenter', 'lotes-layer-status', (e) => {
    map.getCanvas().style.cursor = 'pointer';

    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const featureId = feature.id;

      if (featureId !== undefined && featureId !== null) {
        if (hoveredFeatureId !== null && hoveredFeatureId !== featureId) {
          map.setFeatureState({
            source: 'cloud4geo',
            sourceLayer: 'lotes_areas',
            id: hoveredFeatureId
          }, {
            hover: false
          });
        }

        hoveredFeatureId = featureId;

        map.setFeatureState({
          source: 'cloud4geo',
          sourceLayer: 'lotes_areas',
          id: featureId
        }, {
          hover: true
        });
      }
    }
  });

  map.on('mouseleave', 'lotes-layer-status', () => {
    map.getCanvas().style.cursor = '';

    if (hoveredFeatureId !== null) {
      map.setFeatureState({
        source: 'cloud4geo',
        sourceLayer: 'lotes_areas',
        id: hoveredFeatureId
      }, {
        hover: false
      });
      hoveredFeatureId = null;
    }
  });
};

// Mantener compatibilidad con código existente
export const addSourcesAndLayers = setupMapEventHandlers;

export const getFeaturesInView = (map) => {
  if (!map) return [];
  // Ensure the target layer exists before querying to avoid errors during early load
  try {
    if (!map.getLayer || !map.getLayer("lotes-layer-status")) {
      return [];
    }
  } catch {
    return [];
  }

  const features = map.queryRenderedFeatures({
    layers: ['lotes-layer-status']
  });

  return features.map(feature => {
    const properties = {
      id: feature.properties.id || feature.properties.ID || feature.properties.objectid,
      lot_number: feature.properties.lot_number,
      name: feature.properties.name,
      status: feature.properties.status,
      price: feature.properties.price,
      area_calculated: feature.properties.area_calculated,
      project_id: feature.properties.project_id
    };

    const coordinates = feature.geometry.coordinates[0][0]; // Get first coordinate of polygon
    const centroid = {
      lng: coordinates[0],
      lat: coordinates[1]
    };

    return {
      ...feature,
      properties,
      centroid
    };
  });
};

export const flyToFeatureAndHighlight = (feature, map) => {
  if (!feature || !map) return;

  // Clear any existing highlights
  clearAllHighlights(map);

  // Set new selection - use feature.id if available (promoted from 'id' via promoteId), otherwise use a property
  const featureId = feature.id ?? (feature.properties?.id || feature.properties?.ID || feature.properties?.objectid);
  
  if (featureId) {
    map.setFeatureState({
      source: 'cloud4geo',
      sourceLayer: 'lotes_areas',
      id: featureId
    }, {
      selected: true
    });
  }

  // Zoom automático desactivado - el mapa se mantiene en su posición actual
};

export const clearAllHighlights = (map) => {
  if (!map) return;

  try {
    // Method 1: Clear using querySourceFeatures (all loaded features)
    const features = map.querySourceFeatures('cloud4geo', {
      sourceLayer: 'lotes_areas'
    });

    // Clear feature state for all features - try multiple ID formats
    features.forEach(feature => {
      // Try feature.id first (most reliable)
      if (feature.id !== undefined && feature.id !== null) {
        try {
          map.setFeatureState({
            source: 'cloud4geo',
            sourceLayer: 'lotes_areas',
            id: feature.id
          }, {
            selected: false
          });
        } catch (e) {
          // Ignore errors for individual features
        }
      }
      
      // Also try properties as fallback ID
      const featureId = feature.properties?.id || feature.properties?.ID || feature.properties?.objectid;
      if (featureId !== undefined && featureId !== null && featureId !== feature.id) {
        try {
          map.setFeatureState({
            source: 'cloud4geo',
            sourceLayer: 'lotes_areas',
            id: featureId
          }, {
            selected: false
          });
        } catch (e) {
          // Ignore errors for individual features
        }
      }
    });

    // Method 2: Also clear using queryRenderedFeatures (only visible features)
    // This ensures we clear features that are currently visible on screen
    try {
      const renderedFeatures = map.queryRenderedFeatures({
        layers: ['lotes-layer-status', 'lotes-layer-highlighted']
      });
      
      renderedFeatures.forEach(feature => {
        if (feature.id !== undefined && feature.id !== null) {
          try {
            map.setFeatureState({
              source: 'cloud4geo',
              sourceLayer: 'lotes_areas',
              id: feature.id
            }, {
              selected: false
            });
          } catch (e) {
            // Ignore errors
          }
        }
        
        const featureId = feature.properties?.id || feature.properties?.ID || feature.properties?.objectid;
        if (featureId !== undefined && featureId !== null && featureId !== feature.id) {
          try {
            map.setFeatureState({
              source: 'cloud4geo',
              sourceLayer: 'lotes_areas',
              id: featureId
            }, {
              selected: false
            });
          } catch (e) {
            // Ignore errors
          }
        }
      });
    } catch (e) {
      // Ignore errors for rendered features
    }
  } catch (error) {
    console.warn('Error clearing highlights:', error);
  }
};

export const toggleLotStatusLayer = (map) => {
  if (!map) return;

  const visibility = map.getLayoutProperty(
    "lotes-layer-status",
    "visibility"
  );
  const newVisibility = visibility === "visible" ? "none" : "visible";

  map.setLayoutProperty("lotes-layer-status", "visibility", newVisibility);
};

// Test function to verify highlighting works
export const testHighlighting = (map) => {
  if (!map) return;

  try {
    const allFeatures = map.querySourceFeatures("cloud4geo", {
      sourceLayer: "lotes_areas"
    });

    if (allFeatures.length > 0) {
      const firstFeature = allFeatures[0];
      const testId = firstFeature.id ?? (firstFeature.properties?.id || firstFeature.properties?.ID || firstFeature.properties?.objectid);
      if (testId) {
        map.setFeatureState({
          source: 'cloud4geo',
          sourceLayer: 'lotes_areas',
          id: testId
        }, {
          selected: true
        });
      }
    }
  } catch (error) {
    console.error('Test highlighting error:', error);
  }
};

export const highlightLot = (lotId, map) => {
  if (!map || !lotId) {
    console.warn('%cMap/util.js: highlightLot called with invalid params:', 'color: #FF6B6B;', { map: !!map, lotId });
    return;
  }

  try {
    // Find the feature with the matching lot ID FIRST
    const features = map.querySourceFeatures('cloud4geo', {
      sourceLayer: 'lotes_areas'
    });

    const targetFeature = features.find(f => {
      // Try multiple ways to match the ID (promoteId uses 'id' property)
      const lotIdStr = String(lotId);
      return (
        f.id === lotId ||
        f.id === lotIdStr ||
        String(f.properties?.id) === lotIdStr ||
        String(f.properties?.ID) === lotIdStr ||
        String(f.properties?.objectid) === lotIdStr ||
        String(f.properties?.feature_index) === lotIdStr
      );
    });

    if (!targetFeature) {
      console.warn('%cMap/util.js: Lot not found for highlighting:', 'color: #FF6B6B;', {
        lotId,
        totalFeatures: features.length,
        sampleFeature: features[0] ? {
          id: features[0].id,
          properties: features[0].properties
        } : null
      });
      return;
    }

    // Get the feature ID for the target (promoteId uses 'id' property)
    const newFeatureId = targetFeature.id !== undefined && targetFeature.id !== null
      ? targetFeature.id
      : targetFeature.properties?.id ||
        targetFeature.properties?.ID ||
        targetFeature.properties?.objectid;

    if (newFeatureId === undefined || newFeatureId === null) {
      console.warn('%cMap/util.js: Could not determine feature ID for highlighting:', 'color: #FF6B6B;', targetFeature);
      return;
    }

    // Clear ALL highlights EXCEPT the one we're about to set - this prevents flickering
    features.forEach(feature => {
      const currentId = feature.id !== undefined && feature.id !== null
        ? feature.id
        : feature.properties?.id || feature.properties?.ID || feature.properties?.objectid;

      // Only clear if it's NOT the feature we want to highlight
      if (currentId !== newFeatureId && currentId !== undefined && currentId !== null) {
        try {
          map.setFeatureState({
            source: 'cloud4geo',
            sourceLayer: 'lotes_areas',
            id: currentId
          }, {
            selected: false
          });
        } catch (e) {
          // Ignore errors for individual features
        }
      }
    });

    // Now set the highlight for the new feature - NO DELAY, instant transition
    map.setFeatureState({
      source: 'cloud4geo',
      sourceLayer: 'lotes_areas',
      id: newFeatureId
    }, {
      selected: true
    });

  } catch (error) {
    console.error('%cMap/util.js: Error highlighting lot:', 'color: #FF6B6B;', error);
  }
};

export const flyToLotAndHighlight = (lotId, map) => {
  if (!map || !lotId) return;

  try {
    // Find the feature with the matching lot ID
    const features = map.querySourceFeatures('cloud4geo', {
      sourceLayer: 'lotes_areas'
    });

    const lotIdStr = String(lotId);
    const targetFeature = features.find(f =>
      f.id === lotId ||
      f.id === lotIdStr ||
      String(f.properties?.id) === lotIdStr ||
      String(f.properties?.ID) === lotIdStr ||
      String(f.properties?.objectid) === lotIdStr ||
      f.properties?.lot_number === lotId
    );

    if (targetFeature) {
      // Highlight the lot
      highlightLot(lotId, map);

      // Zoom automático desactivado - el mapa se mantiene en su posición actual
    } else {
      console.warn('Lot not found for highlighting:', lotId);
    }
  } catch (error) {
    console.error('Error highlighting lot:', error);
  }
};

// Store event listener references for cleanup
let markerEventListeners = {
  sourcedata: null,
  idle: null,
  moveend: null,
  zoom: null
};

/**
 * Function to add markers based on vector tile data
 */
export const addMarkersFromVectorTile = (map, projectId = null) => {
  if (!map) return;

  try {
    // Remove previous event listeners to prevent accumulation
    if (markerEventListeners.sourcedata) {
      map.off('sourcedata', markerEventListeners.sourcedata);
    }
    if (markerEventListeners.moveend) {
      map.off('moveend', markerEventListeners.moveend);
    }
    if (markerEventListeners.zoom) {
      map.off('zoom', markerEventListeners.zoom);
    }

    // Clear existing markers ONLY for this project
    const existingMarkers = document.querySelectorAll('.custom-marker');
    existingMarkers.forEach(marker => {
      // Only remove if it's for the same project or if we're removing all markers
      if (!marker.dataset.projectId || marker.dataset.projectId === String(projectId)) {
        marker.remove();
      }
    });

    // Track processed markers to avoid duplicates - use coordinates as key for more reliability
    let processedMarkers = new Set();

    // Retry control for when vector tiles aren't ready yet
    let retryCount = 0;
    const MAX_RETRIES = 10;
    const RETRY_DELAY_MS = 800;

    // Function to process markers from vector tiles
    const processMarkers = () => {
      try {
        // Query features from the Martin vector tile source
        const allFeatures = map.querySourceFeatures('lotespro-markers', {
          sourceLayer: 'lotespro_markers'
        });

        // Log sample project_ids from tiles for debugging
        if (allFeatures.length > 0) {
          const sampleIds = allFeatures.slice(0, 3).map(f => f.properties?.project_id);
        }

        // Filter by project_id if provided
        // Handle UUID comparison flexibly (full UUID vs truncated)
        const features = projectId
          ? allFeatures.filter(f => {
              const featureProjectId = String(f.properties?.project_id || '');
              const targetProjectId = String(projectId);

              // Exact match
              if (featureProjectId === targetProjectId) return true;

              // Check if one starts with the other (for truncated UUIDs)
              if (featureProjectId.startsWith(targetProjectId)) return true;
              if (targetProjectId.startsWith(featureProjectId)) return true;

              return false;
            })
          : allFeatures;


        if (features.length === 0) {
          // Vector tiles may not be loaded yet - retry up to MAX_RETRIES times
          if (retryCount < MAX_RETRIES) {
            retryCount++;
            setTimeout(processMarkers, RETRY_DELAY_MS);
          } else {
          }
          return;
        }

        // Process each marker from vector tiles
        features.forEach((feature, index) => {
          const properties = feature.properties || {};

          // Get coordinates from geometry first - we need this for unique ID
          let coordinates = null;
          if (feature.geometry && feature.geometry.coordinates) {
            if (feature.geometry.type === 'Point') {
              coordinates = feature.geometry.coordinates;
            }
          }

          if (!coordinates) {
            return;
          }

          // Create unique ID using coordinates + marker type for more reliable deduplication
          // This prevents duplicates from tile boundaries where feature.id can differ
          let markerType = properties.marker_type || properties.type || 'default';
          const coordKey = `${coordinates[0].toFixed(6)}_${coordinates[1].toFixed(6)}`;
          const markerId = properties.id
            ? `${properties.id}`
            : `${markerType}_${coordKey}`;

          // Skip if already processed
          if (processedMarkers.has(markerId)) {
            return;
          }
          processedMarkers.add(markerId);
          let markerText = properties.name || properties.text || properties.label || `Marker ${index + 1}`;
          // Extract image URL if available
          let imageUrl = properties.image_url || properties.imageUrl || properties.image || null;
          
          // Map marker types to our icon types
          switch (markerType.toLowerCase()) {
            case 'ingreso':
            case 'entrance':
            case 'entrada':
              markerType = 'ingreso';
              break;
            case 'salida':
            case 'exit':
              markerType = 'salida';
              break;
            case 'etapa':
            case 'stage':
            case 'phase':
              markerType = 'etapa';
              break;
            case 'bloque':
            case 'block':
            case 'building':
            case 'manzana':
              markerType = 'bloque';
              break;
            case 'estacionamiento':
            case 'parking':
              markerType = 'estacionamiento';
              break;
            case 'area_verde':
            case 'green_area':
            case 'green':
              markerType = 'area_verde';
              break;
            case 'arboles':
            case 'trees':
            case 'tree':
              markerType = 'arboles';
              break;
            case 'punto_interes':
            case 'punto de interés':
            case 'point_of_interest':
            case 'poi':
              markerType = 'punto_interes';
              break;
            case 'punto_foto':
            case 'punto_fotos':
            case 'punto foto':
            case 'punto fotos':
            case 'photo_point':
            case 'foto':
            case 'fotos':
            case 'photo':
            case 'photos':
              markerType = 'punto_foto';
              break;
            default:
              // Si no coincide, intentar usar el tipo original o default
              markerType = 'default';
          }

          // Skip marker types rendered by symbol layers in mapStyle.json
          // These are loaded from map-styles service for better performance
          const symbolLayerTypes = [
            'arboles', 'bloque'
          ];
          if (symbolLayerTypes.includes(markerType)) {
            return; // Rendered by symbol layer
          }

          // Create HTML marker for remaining types (area_verde, default)
          const markerConfig = MARKER_TYPES[markerType] || MARKER_TYPES.default;
          
          if (!markerConfig) {
            return; // Skip this marker if no config found
          }
          
          const iconConfig = createCustomMarkerIcon(
            markerType,
            markerConfig.color,
            markerText || markerConfig.defaultText,
            markerConfig.textColor,
            imageUrl // Pass image URL for punto_interes and punto_foto markers
          );
          
          if (!iconConfig || !iconConfig.html) {
            return; // Skip this marker if icon creation failed
          }

          // Create marker element
          const markerEl = document.createElement('div');
          markerEl.innerHTML = iconConfig.html;
          markerEl.className = iconConfig.className;
          markerEl.dataset.projectId = projectId; // Add project ID to marker
          markerEl.style.cssText = `
            width: ${iconConfig.iconSize[0]}px;
            height: ${iconConfig.iconSize[1]}px;
            cursor: pointer;
            z-index: 10;
          `;

          // Add click handler
          markerEl.addEventListener('click', () => {
            // Special handling for punto_foto with image - open image modal
            if (markerType === 'punto_foto' && imageUrl) {
              // Dispatch custom event to open image modal
              const event = new CustomEvent('openImageModal', {
                detail: { imageUrl, markerText: markerText || 'Punto de Foto' }
              });
              window.dispatchEvent(event);
              return;
            }
            
            // Special handling for area_verde, estacionamiento, and arboles - show simple popup
            if (markerType === 'area_verde' || markerType === 'estacionamiento' || markerType === 'arboles') {
              // Create simple popup
              const popup = new mapboxgl.Popup({
                closeButton: true,
                closeOnClick: true,
                className: 'simple-marker-popup'
              });

              let popupText = markerText || 'Marcador';
              if (markerType === 'area_verde') popupText = markerText || 'Área Verde';
              if (markerType === 'estacionamiento') popupText = markerText || 'Estacionamiento';
              if (markerType === 'arboles') popupText = markerText || 'Zona de Árboles';

              popup.setHTML(`
                <div style="padding: 8px 12px; font-family: Arial, sans-serif; font-size: 14px; font-weight: bold; color: #333;">
                  ${popupText}
                </div>
              `);

              // Set popup to marker position
              popup.setLngLat(coordinates).addTo(map);
              return;
            }
            
            // Default behavior for other markers
            if (markerId) {
              flyToLotAndHighlight(markerId, map);
            }
          });

          // Create Mapbox marker with correct anchor offset
          // iconAnchor defines which pixel of the icon sits on the coordinate
          // Convert to Mapbox offset: shift from center to the anchor point
          const offsetX = iconConfig.iconAnchor[0] - iconConfig.iconSize[0] / 2;
          const offsetY = iconConfig.iconAnchor[1] - iconConfig.iconSize[1] / 2;
          const mapboxMarker = new mapboxgl.Marker({ element: markerEl, offset: [offsetX, offsetY] })
            .setLngLat(coordinates)
            .addTo(map);

          // Determine minzoom based on marker type
          const minZoom = 14;

          // Store marker reference and minZoom for zoom control
          markerEl._mapboxMarker = mapboxMarker;
          markerEl._minZoom = minZoom;

          // Set initial visibility based on current zoom
          const currentZoom = map.getZoom();
          markerEl.style.display = currentZoom >= minZoom ? 'block' : 'none';
        });
      } catch (error) {
        console.error('[Markers] Error processing markers:', error);
      }
    };

    // Function to check and process markers when source is ready
    const checkAndProcessMarkers = () => {
      const source = map.getSource('lotespro-markers');
      if (!source) {
        return false;
      }

      processMarkers();
      return true;
    };

    // Setup event listeners for when tiles load
    const setupEventListeners = () => {
      let processed = false;

      // Listen for sourcedata event
      const onSourceData = (e) => {
        if (processed) return;
        if (e.sourceId === 'lotespro-markers' && e.isSourceLoaded) {
          setTimeout(() => {
            if (!processed) {
              processed = true;
              processMarkers();
              map.off('sourcedata', onSourceData);
            }
          }, 100);
        }
      };

      // Also listen for idle event as fallback
      const onIdle = () => {
        if (processed) return;
        setTimeout(() => {
          if (!processed && map.getSource('lotespro-markers')) {
            processed = true;
            processMarkers();
          }
        }, 200);
      };

      // Listen for moveend to process new tiles when panning/zooming
      const onMoveEnd = () => {
        setTimeout(() => {
          // Clear the processedMarkers Set to allow re-processing markers from new tiles
          processedMarkers.clear();
          // Clear existing markers for this project before re-processing
          const existingMarkers = document.querySelectorAll('.custom-marker');
          existingMarkers.forEach(marker => {
            if (!marker.dataset.projectId || marker.dataset.projectId === String(projectId)) {
              marker.remove();
            }
          });
          processMarkers();
        }, 200);
      };

      // Store references for cleanup
      markerEventListeners.sourcedata = onSourceData;
      markerEventListeners.moveend = onMoveEnd;

      map.on('sourcedata', onSourceData);
      map.once('idle', onIdle);
      map.on('moveend', onMoveEnd);
    };

    // Try immediately if map is loaded
    if (map.loaded() && map.getSource('lotespro-markers')) {
      setTimeout(() => {
        if (!checkAndProcessMarkers()) {
          setupEventListeners();
        }
      }, 200);
    } else {
      setupEventListeners();
    }

    // Add zoom event listener to show/hide markers based on zoom level
    const handleZoomChange = () => {
      const currentZoom = map.getZoom();
      const markerElements = document.querySelectorAll('.custom-marker');

      markerElements.forEach(markerEl => {
        const minZoom = markerEl._minZoom || 16; // Default to 16 if not set

        // Show/hide markers using CSS display property
        if (currentZoom >= minZoom) {
          markerEl.style.display = 'block';
        } else {
          markerEl.style.display = 'none';
        }
      });
    };

    // Store reference for cleanup
    markerEventListeners.zoom = handleZoomChange;

    // Listen to zoom events
    map.on('zoom', handleZoomChange);

  } catch (error) {
    console.error('[Markers] Error loading markers:', error);
  }
};
